<template>
    <div class="overview-focus w-100">
        <AonCard :title="currentSelection" :desc="currentSelectionDescription">
            <template #header-append>
                <ArmoryOptionToggle
                    class="ml-lg mt-sm"
                    style="min-width: 300px"
                    v-model="toggleValue"
                    :options="[
                        { label: 'Moat Coverage', value: 'innovation' },
                        { label: 'Filings', value: 'filings' },
                        { label: 'Timeline', value: 'timeline' },
                    ]"
                    size="sm"
                    @update:modelValue="onToggleChange"
                />
            </template>
            <div class="card-content">
                <div v-if="toggleValue === 'innovation'">
                    <InnovationFocus :data="dataToUse" :data-by-year="dataByYear" />
                </div>
                <div v-if="toggleValue === 'filings'">
                    <FilingsFocus :data="dataToUse" />
                </div>
                <div v-if="toggleValue === 'timeline'">
                    <InnovationOverTime
                        tab="strategy"
                        :node-type="entityStore.strategyTabNodeFilter"
                    />
                </div>
            </div>
        </AonCard>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useEntityStore } from '@/stores'

const entityStore = useEntityStore()

import InnovationFocus from './InnovationFocus.vue'
import FilingsFocus from './FilingsFocus.vue'
import InnovationOverTime from '@/components/alignmentOutput/techAreas/InnovationOverTime.vue'

const overwriteData = ref([])
const toggleValue = ref('innovation')

const props = defineProps({
    productData: {
        type: Array,
        required: true,
    },
    technologyData: {
        type: Array,
        required: true,
    },
    dataByYearProduct: {
        type: Array,
        required: true,
    },
    dataByYearTechnology: {
        type: Array,
        required: true,
    },
})

const dataToUse = computed(() => {
    return entityStore.strategyTabNodeFilter === 'product'
        ? props.productData
        : props.technologyData
})

const dataByYear = computed(() => {
    return entityStore.strategyTabNodeFilter === 'product'
        ? props.dataByYearProduct
        : props.dataByYearTechnology
})

const currentSelection = computed(() => {
    return toggleValue.value === 'innovation'
        ? 'Moat Coverage'
        : toggleValue.value === 'filings'
          ? 'Filings'
          : 'Timeline'
})

const currentSelectionDescription = computed(() => {
    if (entityStore.strategyTabNodeFilter === 'product') {
        return toggleValue.value === 'innovation'
            ? `This chart visualizes ${entityStore.entity.name}'s Moat Coverage over time, highlighting when patenting activity began, peaked, and declined for each product class. The thickness of each band represents the volume or impact of filings in that product area.`
            : toggleValue.value === 'filings'
              ? `This timeline shows ${entityStore.entity.name}'s patent filings across different product areas. It marks the first and last patent dates, the duration of filing activity, and the average filing date for each product. Peer averages are included for comparison.`
              : `This view illustrates how ${entityStore.entity.name}'s product-focused patent strategy has evolved over time.`
    } else {
        return toggleValue.value === 'innovation'
            ? `This chart illustrates ${entityStore.entity.name}'s Moat Coverage across technologies over time. It shows when patenting activity began, intensified, and slowed. The thickness of each band reflects the filing volume or impact in each technology category.`
            : toggleValue.value === 'filings'
              ? `This timeline shows ${entityStore.entity.name}'s patent filing history across technologies. It includes the first and last filing dates, filing duration, and average filing date per technology. Peer averages provide additional context.`
              : `This view shows how ${entityStore.entity.name}'s technology investment strategy has shifted over time.`
    }
})

const onToggleChange = (value) => {
    toggleValue.value = value
}
</script>

<style lang="scss" scoped>
.overview-focus {
    position: relative;
}
</style>
